import { getParamValue, isCollectibleBasedIdentification, type AiIdentification } from "@/services/aiIdentification";
import { CategoryId } from "@/services/collection/categories";
import { computedAsync } from "@vueuse/core";
import type { Ref } from "vue";
import { useGettext } from "vue3-gettext";
import { Parameters } from "@/services/collection/parameters";
import { getCollectibleElasticParams } from "@/services/collectibles/elastic/collectiblesElasticHelpers";

type Param = {
  name: string;
  value: string | number | undefined;
};

export function useAiIdentificationParams(identification: Ref<AiIdentification | undefined>) {
  const { $gettext } = useGettext();

  const params = computedAsync(async (): Promise<Param[]> => {
    if (identification.value === undefined) {
      return [];
    }

    if (identification.value.categoryId === CategoryId.LegoMinifigures) {
      const obsoleteParameters = await getObsoleteParameters(identification.value);
      return [
        { name: $gettext("Číslo"), value: identification.value.catalogueItem.number }, //
        { name: $gettext("Počet kostiček"), value: identification.value.catalogueItem.partsCount },
        ...obsoleteParameters,
      ];
    }

    if (identification.value.categoryId === CategoryId.LegoSets) {
      const obsoleteParameters = await getObsoleteParameters(identification.value);
      return [
        { name: $gettext("Téma"), value: identification.value.catalogueItem.themeName }, //
        { name: $gettext("Číslo"), value: identification.value.catalogueItem.number },
        { name: $gettext("Počet kostiček"), value: identification.value.catalogueItem.partsCount },
        { name: $gettext("Rok vydání"), value: identification.value.catalogueItem.year },
        { name: $gettext("Rozměry krabice"), value: identification.value.catalogueItem.dimensions },
        { name: $gettext("Hmotnost"), value: identification.value.catalogueItem.weight },
        ...obsoleteParameters,
      ];
    }

    if (isCollectibleBasedIdentification(identification.value)) {
      return getCollectibleElasticParams(identification.value.collectible);
    }

    const obsoleteParameters = await getObsoleteParameters(identification.value);
    return obsoleteParameters;
  });

  return params;
}

const getObsoleteParameters = async (identification: AiIdentification): Promise<Param[]> => {
  const categoryId = identification.categoryId;
  const params = await Parameters.Api.fetchForCategory(categoryId);
  return params.map((param) => ({
    name: param.name,
    value: getParamValue(param, identification),
  }));
};
